// TEST-EXEMPT: Visual/animation-only component with no testable logic
/**
 * StickyStack — Pure CSS stacking cards via position: sticky
 *
 * Follows React best practices (no direct DOM mutation, no scroll hijacking,
 * no Lenis, no requestAnimationFrame loops). Uses native scroll + CSS only.
 *
 * Each card pins at a staggered `top` offset, creating the illusion of cards
 * stacking on top of each other as the user scrolls.
 *
 * @see https://github.com/vasanthk/react-bits — anti-patterns: findDOMNode, mutating state
 * @see Vercel best practices: js-batch-dom-css, rendering-content-visibility
 */
import { Children, type ReactNode } from 'react'

interface StickyStackProps {
  children: ReactNode
  /** CSS class for the outer container */
  className?: string
  /** Top offset for the first card (px) */
  topOffset?: number
  /** Vertical gap between stacked card tops (px) */
  stackGap?: number
  /** Horizontal padding class */
  paddingClass?: string
}

interface StickyStackItemProps {
  children: ReactNode
  /** Additional class names */
  className?: string
}

export function StickyStackItem({ children, className = '' }: StickyStackItemProps) {
  return <div className={className}>{children}</div>
}

export default function StickyStack({
  children,
  className = '',
  topOffset = 32,
  stackGap = 28,
  paddingClass = 'px-6 sm:px-12 lg:px-20',
}: StickyStackProps) {
  const items = Children.toArray(children)

  return (
    <div className={`relative ${className}`}>
      {items.map((child, index) => (
        <div
          key={index}
          className={`sticky ${paddingClass} pb-8`}
          style={{
            top: `${topOffset + index * stackGap}px`,
            zIndex: index + 1, // Later cards stack on top
          }}
        >
          {/* Shadow/depth illusion — card appears to "settle" as it pins */}
          <div
            className="mx-auto max-w-5xl transition-shadow duration-500"
            style={{
              // Subtle depth: deeper cards (lower index) get slight scale when covered
              // Using CSS will-change for GPU acceleration
              willChange: 'auto',
            }}
          >
            {child}
          </div>
        </div>
      ))}
      {/* Spacer to give last card room to pin before footer */}
      <div className="h-[30vh]" />
    </div>
  )
}
